import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, DAG_RUN_TASK_DETAIL_TAB, DagRunTaskDetailTab, MAP_INDEX_NONE } from '../constants';
import { AirflowTaskInstance } from '../types';

type ConfirmActionType = 'success' | 'failed';

/*
 * Manages the DAG-run-task detail screen's header data, active tab, map-index-aware routing,
 * and the clear/mark-success/mark-failed actions shared by the header across every tab
 */
export function useDagRunTaskDetail() {
    // react-router-dom v5's useParams does not decode dynamic segments — they arrive exactly
    // as they appeared in the URL (still percent-encoded, e.g. run IDs like
    // "scheduled__2026-08-13T06:45:00+00:00" become "...T06%3A45%3A00%2B00%3A00"), so encoding
    // them again for an API call would double-encode into "%253A" and 404. Decode once here.
    const {
        dagId: rawDagId,
        runId: rawRunId,
        taskId: rawTaskId,
    } = useParams<{ dagId: string; runId: string; taskId: string }>();
    const dagId = decodeURIComponent(rawDagId);
    const runId = decodeURIComponent(rawRunId);
    const taskId = decodeURIComponent(rawTaskId);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();
    const location = useLocation();

    const [taskInstance, setTaskInstance] = useState<AirflowTaskInstance | null>(null);
    const [isLoadingTask, setIsLoadingTask] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [clearing, setClearing] = useState(false);
    const [confirmAction, setConfirmAction] = useState<ConfirmActionType | null>(null);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const mapIndex = useMemo(() => {
        const raw = searchParams.get('map_index');
        return raw !== null ? Number(raw) : MAP_INDEX_NONE;
    }, [searchParams]);

    const activeTab = useMemo<DagRunTaskDetailTab>(() => (searchParams.get('tab') as DagRunTaskDetailTab) || DAG_RUN_TASK_DETAIL_TAB.logs, [searchParams]);

    const handleTabChange = useCallback(
        (tab: string) => {
            const params = new URLSearchParams(location.search);
            params.set('tab', tab);
            history.replace({ pathname: location.pathname, search: params.toString() });
        },
        [history, location.pathname, location.search]
    );

    const fetchTaskInstance = useCallback(async () => {
        try {
            const response = await AirflowDashboardHandler.getRunTaskInstance(Env, dagId, runId, taskId, mapIndex);
            setTaskInstance(response);
        } catch {
            errorAlert(`Failed to load task instance "${taskId}".`);
        } finally {
            setIsLoadingTask(false);
        }
    }, [Env, dagId, runId, taskId, mapIndex]);

    useEffect(() => {
        fetchTaskInstance();
    }, [dagId, runId, taskId, mapIndex]);

    /*
     * Re-fetches the task instance and bumps the refresh key every tab watches
     */
    const handleRefresh = useCallback(() => {
        fetchTaskInstance();
        setRefreshKey((key) => key + 1);
    }, [fetchTaskInstance]);

    /*
     * Opens the Clear Task dialog
     */
    const handleClearRequest = useCallback(() => setClearing(true), []);

    /*
     * Closes the Clear Task dialog without refreshing (plain cancel)
     */
    const handleCloseClearDialog = useCallback(() => setClearing(false), []);

    /*
     * Refreshes the task instance + every tab after a successful clear
     */
    const handleTaskCleared = useCallback(() => {
        handleRefresh();
    }, [handleRefresh]);

    /*
     * Executes the confirmed mark-success/mark-failed action
     */
    const handleConfirmAction = useCallback(async () => {
        if (!confirmAction) return;
        try {
            await AirflowDashboardHandler.patchTaskInstance(Env, dagId, runId, taskId, { new_state: confirmAction, map_index: mapIndex });
            successAlert(`Marked "${taskId}" as ${confirmAction}.`);
            setConfirmAction(null);
            handleRefresh();
        } catch {
            errorAlert(`Failed to ${confirmAction} task "${taskId}".`);
        }
    }, [Env, dagId, runId, taskId, mapIndex, confirmAction, handleRefresh]);

    /*
     * Navigates back to the DAG-run detail screen
     */
    const handleBack = useCallback(() => {
        history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}/runs/${encodeURIComponent(runId)}`);
    }, [history, dagId, runId]);

    return {
        dagId,
        runId,
        taskId,
        mapIndex,
        taskInstance,
        isLoadingTask,
        refreshKey,
        clearing,
        confirmAction,
        setConfirmAction,
        activeTab,
        handleTabChange,
        handleRefresh,
        handleClearRequest,
        handleCloseClearDialog,
        handleTaskCleared,
        handleConfirmAction,
        handleBack,
    };
}
